import React from "react";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import useMixin from "../../hooks/useMixin";
import useOpen from "../../hooks/useOpen";
import { isLoading, loading, Loading } from "../../Loading";
import { VisitInstanceDetails } from "../../messages";
import { VisitInstanceId } from "../../messages/ObjectIds";
import { AppState } from "../../system/AppState";
import { flmap } from "../../utils/fmap";
import { CicoModalEntity, extractVisitFromCicoModalEntity } from "../../utils/visitUtils";
import ChangesSavedBanner from "../ChangesSavedBanner";
import { UseContactSupportReturnType } from "../useContactSupport";
import MedflytVisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/MedflytVisitLetsStartBottomSheet";
import VisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/VisitLetsStartBottomSheet";
import { MultiVisitsReminderBottomSheet } from "../VisitReminderBottomSheet/MultiVisitsReminderBottomSheet";
import { VisitReminderBottomSheet } from "../VisitReminderBottomSheet/VisitReminderBottomSheet";
import WithDutySheet from "../WithDutySheet";
import WithLetsStartBottomSheet from "../WithLetsStartBottomSheet";
import WithMedflytLetsStartBottomSheet from "../WithMedflytLetsStartBottomSheet";
import WithVisitReminderBottomSheet from "../WithVisitReminderBottomSheet";

export function useActiveVisitBottomSheetsSubActivity(props: {
  api: API;
  appState: AppState;
  contactSupport: UseContactSupportReturnType | Loading;
}) {
  const [activeEntity, setActiveEntity] = React.useState<CicoModalEntity | Loading>(loading());
  const activeVisit = isLoading(activeEntity)
    ? activeEntity
    : extractVisitFromCicoModalEntity(activeEntity);
  const changesSavedBannerDisclosure = useOpen();
  const visitBottomSheetDisclosure = useOpen();
  const visitReminderDisclosure = useOpen();

  const openVisitBottomSheet = (entity: CicoModalEntity) => {
    setActiveEntity(entity);
    visitBottomSheetDisclosure.actions.open();
  };

  const setActiveVisitById = (visitInstanceId: VisitInstanceId) => {
    const visitInstance = flmap(props.appState.scheduledVisits, (x) =>
      x.scheduledVisitDetails.find((x) => x.id === visitInstanceId)
    );

    if (visitInstance === undefined || isLoading(visitInstance)) {
      return;
    }

    openVisitBottomSheet({ type: "visit", visit: visitInstance });
  };

  const submitDutySheetById = (visitInstanceId: VisitInstanceId) => {
    props.appState.setVisitInstanceDutySheetCompleted(visitInstanceId, true);
    if (!isLoading(activeVisit)) {
      setActiveEntity({ type: "visit", visit: { ...activeVisit, isDutySheetCompleted: true } });
    }
  };

  return {
    activeEntity,
    activeVisit,
    setActiveVisitById,
    submitDutySheetById,
    visitBottomSheetDisclosure,
    changesSavedBannerDisclosure,
    openVisitBottomSheet,
    visitReminderDisclosure,
  };
}

export default function ActiveVisitBottomSheetsSubActivity(props: {
  api: API;
  appState: AppState;
  contactSupport: UseContactSupportReturnType | Loading;
  hookProps: ReturnType<typeof useActiveVisitBottomSheetsSubActivity>;
}) {
  const caregiver = useCaregiver(props.appState);
  const mixin = useMixin(props.appState);
  const {
    activeVisit,
    activeEntity,
    visitBottomSheetDisclosure,
    changesSavedBannerDisclosure,
    setActiveVisitById,
    submitDutySheetById,
    openVisitBottomSheet,
    visitReminderDisclosure,
  } = props.hookProps;

  const visitLetsStartBottomSheet = (() => {
    if (isLoading(activeEntity) || isLoading(activeVisit) || isLoading(caregiver)) {
      return null;
    }

    if (caregiver.isCaregiverOfMedFlyt) {
      return (
        <WithDutySheet activeVisit={activeVisit} api={props.api}>
          {(initialDutySheetData) => (
            <WithMedflytLetsStartBottomSheet
              activeEntity={activeEntity}
              api={props.api}
              appState={props.appState}
              initialDutySheetData={initialDutySheetData}
              isOpen={visitBottomSheetDisclosure.isOpen}
              onHideOrShowVisitBottomSheet={visitBottomSheetDisclosure.actions.set}
              onOpenChangesSavedBanner={changesSavedBannerDisclosure.actions.open}
              onUpdateActiveVisit={setActiveVisitById}
              onSubmitDutySheet={submitDutySheetById}
            >
              {(bottomSheetProps) => (
                <MedflytVisitLetsStartBottomSheet
                  {...bottomSheetProps}
                  contactSupport={props.contactSupport}
                />
              )}
            </WithMedflytLetsStartBottomSheet>
          )}
        </WithDutySheet>
      );
    }

    return (
      <WithDutySheet activeVisit={activeVisit} api={props.api}>
        {(initialDutySheetData) => (
          <WithLetsStartBottomSheet
            activeVisit={activeVisit}
            api={props.api}
            appState={props.appState}
            initialDutySheetData={initialDutySheetData}
            isOpen={visitBottomSheetDisclosure.isOpen}
            onHideOrShowVisitBottomSheet={visitBottomSheetDisclosure.actions.set}
            onOpenChangesSavedBanner={changesSavedBannerDisclosure.actions.open}
            onUpdateActiveVisit={setActiveVisitById}
            onSubmitDutySheet={submitDutySheetById}
          >
            {(bottomSheetProps) => (
              <VisitLetsStartBottomSheet
                {...bottomSheetProps}
                contactSupport={props.contactSupport}
              />
            )}
          </WithLetsStartBottomSheet>
        )}
      </WithDutySheet>
    );
  })();

  const visitReminderBottomSheet = (
    <WithVisitReminderBottomSheet
      scheduledVisitsState={mixin.scheduledVisits}
      onClickStartNow={(visit) => openVisitBottomSheet({ type: "visit", visit })}
      onRequestClose={visitReminderDisclosure.actions.dismiss}
      onRequestOpen={visitReminderDisclosure.actions.open}
    >
      {(reminderProps, presentVisits) => {
        return presentVisits.length() > 1 ? (
          <MultiVisitsReminderBottomSheet
            caregiver={caregiver}
            isOpen={visitReminderDisclosure.isOpen}
            presentVisits={presentVisits}
            onClickStartNow={reminderProps.onClickStartNow}
            onRequestClose={reminderProps.onRequestClose}
          />
        ) : (
          <VisitReminderBottomSheet
            isOpen={visitReminderDisclosure.isOpen}
            presentVisit={presentVisits.first() as VisitInstanceDetails}
            onClickStartNow={reminderProps.onClickStartNow}
            onRequestClose={reminderProps.onRequestClose}
          />
        );
      }}
    </WithVisitReminderBottomSheet>
  );

  return (
    <>
      {visitLetsStartBottomSheet}
      {visitReminderBottomSheet}
      <ChangesSavedBanner
        isOpen={changesSavedBannerDisclosure.isOpen}
        onClose={changesSavedBannerDisclosure.actions.dismiss}
      />
    </>
  );
}
